import React, { useState, useRef, useEffect } from 'react';
import { Button } from './Button';
import { useLicenseGate } from './LicenseGate';

export interface ColorPickerProps {
  label?: string;
  value?: string;
  onChange?: (color: string) => void;
  disabled?: boolean;
  className?: string;
  showPresets?: boolean;
  presetColors?: string[];
  disableOnLicenseExpired?: boolean;
  required?: boolean;
}

const DEFAULT_PRESET_COLORS = [
  '#000000', '#FFFFFF', '#FF0000', '#00FF00', '#0000FF',
  '#FFFF00', '#FF00FF', '#00FFFF', '#FFA500', '#800080',
  '#FFC0CB', '#A52A2A', '#808080', '#000080', '#008000',
  '#FFD700', '#DC143C', '#00CED1', '#FF6347', '#32CD32'
];

export const ColorPicker: React.FC<ColorPickerProps> = ({
  label,
  value = '#ffffff',
  onChange,
  disabled = false,
  className = '',
  showPresets = true,
  presetColors = DEFAULT_PRESET_COLORS,
  disableOnLicenseExpired = true,
  required
}) => {
  const { shouldDisable } = useLicenseGate();
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState(value);
  const [isValidColor, setIsValidColor] = useState(true);
  const colorInputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);

  const handleColorChange = (newColor: string) => {
    setInputValue(newColor);
    setIsValidColor(true);
    onChange?.(newColor);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    
    const isValid = /^#[0-9A-Fa-f]{6}$|^#[0-9A-Fa-f]{3}$/.test(newValue);
    setIsValidColor(isValid);
    
    if (isValid) {
      onChange?.(newValue);
    }
  };

  const handlePresetClick = (color: string) => {
    handleColorChange(color);
    setIsOpen(false);
  };

  const togglePicker = () => {
    if (!disabled && !(disableOnLicenseExpired && shouldDisable)) {
      setIsOpen(!isOpen);
    }
  };

  const formatColor = (color: string): string => {
    if (color.startsWith('#')) {
      return color.toUpperCase();
    }
    return `#${color.toUpperCase()}`;
  };

  return (
    <div className={`w-full ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-secondary-700 mb-2">
          {label}
            {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        <div className="flex items-center space-x-2">
          <button
            type="button"
            onClick={togglePicker}
            disabled={disabled || (disableOnLicenseExpired && shouldDisable)}
            className={`
              w-12 h-10 rounded-md border-2 border-secondary-300 
              flex items-center justify-center transition-colors
              ${disabled || (disableOnLicenseExpired && shouldDisable) ? 'cursor-not-allowed opacity-50' : 'cursor-pointer hover:border-secondary-400'}
            `}
            style={{ backgroundColor: formatColor(inputValue) }}
          >
            <div 
              className="w-8 h-6 rounded border border-gray-300"
              style={{ backgroundColor: formatColor(inputValue) }}
            />
          </button>
          
          <div className="flex-1">
            <input
              ref={colorInputRef}
              type="text"
              value={inputValue}
              onChange={handleInputChange}
              disabled={disabled || (disableOnLicenseExpired && shouldDisable)}
              placeholder="#ffffff"
              className={`
                w-full px-3 py-2 border rounded-md text-sm font-mono
                ${isValidColor ? 'border-secondary-300' : 'border-red-300 bg-red-50'}
                ${disabled || (disableOnLicenseExpired && shouldDisable) ? 'bg-secondary-50 cursor-not-allowed' : 'bg-white'}
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent
              `}
            />
            {!isValidColor && (
              <p className="text-xs text-red-600 mt-1">Invalid color format</p>
            )}
          </div>
        </div>

        {isOpen && (
          <div className="absolute top-full left-0 mt-2 p-4 bg-white border border-secondary-200 rounded-lg shadow-lg z-50 min-w-64">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  Color Picker
                </label>
                <input
                  type="color"
                  value={formatColor(inputValue)}
                  onChange={(e) => handleColorChange(e.target.value)}
                  className="w-full h-10 border border-secondary-300 rounded cursor-pointer"
                />
              </div>

              {showPresets && (
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Preset Colors
                  </label>
                  <div className="grid grid-cols-5 gap-2">
                    {presetColors.map((color) => (
                      <button
                        key={color}
                        type="button"
                        onClick={() => handlePresetClick(color)}
                        className="w-8 h-8 rounded border border-secondary-300 hover:border-secondary-400 transition-colors"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsOpen(false)}
                >
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColorPicker;
